/**
 * AWPlayer Deep Link Trigger
 * Supports deep linking to specific tracks in AWPlayer via query params or hash.
 * Example: ?awplay=1 or #awplay=1 or #play-audio&awplay=1
 */

(function() {
  'use strict';

  /**
   * Parse awplay track index from a URL
   * @param {string} urlStr 
   * @returns {object|null} { trackIndex } or null
   */
  function parseAwplayFromUrl(urlStr) {
    if (!urlStr) return null;
    try {
      const url = new URL(urlStr, window.location.origin);
      
      // Try query param
      let trackIndex = url.searchParams.get('awplay');
      
      // If not in query, try hash
      if (!trackIndex && url.hash) {
        // Remove leading # and replace common separators with & to parse as search params
        const hashStr = url.hash.substring(1).replace(/[&;]/g, '&');
        const hashParams = new URLSearchParams(hashStr);
        trackIndex = hashParams.get('awplay');
      }

      if (trackIndex) {
        const index = parseInt(trackIndex, 10);
        if (!isNaN(index)) {
          return { trackIndex: index };
        }
      }
    } catch (e) {
      console.error('Error parsing URL:', e);
    }
    return null;
  }

  /**
   * Find matching links and add styling class
   * @param {HTMLElement} root 
   */
  function findAndStyleAwplayLinks(root) {
    const links = root.querySelectorAll('a[href*="awplay"]');
    links.forEach(a => {
      const info = parseAwplayFromUrl(a.getAttribute('href'));
      if (info) {
        a.classList.add('reply-awplay-link');
      }
    });
  }

  /**
   * Wait for AWPlayer DOM to be ready
   * @returns {Promise}
   */
  function waitForAwplayerDom() {
    return new Promise((resolve) => {
      const selectors = ['.awplayer', '[data-awplayer]', 'awplayer-app', 'iframe[src*="awplayer"]'];
      
      const checkDom = () => {
        for (const selector of selectors) {
          const el = document.querySelector(selector);
          if (el) {
            // If it's an iframe, we might need more logic, but for now assume DOM ready
            // If there's a playlist, wait for items
            const playlistItems = document.querySelectorAll('button[aria-label*="Play"], .play-button, .track-play, .awp-track, .awp-playlist-item');
            if (playlistItems.length > 0) {
              return true;
            }
          }
        }
        return false;
      };

      if (checkDom()) {
        resolve();
        return;
      }

      const observer = new MutationObserver((mutations, obs) => {
        if (checkDom()) {
          obs.disconnect();
          resolve();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Timeout after 10 seconds to avoid hanging
      setTimeout(() => {
        observer.disconnect();
        resolve();
      }, 10000);
    });
  }

  /**
   * Trigger AWPlayer to play a specific track
   * @param {number} trackIndex 1-based index
   */
  function playAwplayerTrack(trackIndex) {
    const container = document.querySelector('.awplayer, [data-awplayer], awplayer-app');
    if (!container) {
      // Fallback: search globally if container not found by specific selectors
      const buttons = document.querySelectorAll('button[aria-label*="Play"], .play-button, .track-play');
      if (buttons.length >= trackIndex) {
        buttons[trackIndex - 1].click();
      }
      return;
    }

    // Try finding play buttons within the container
    let playButtons = container.querySelectorAll('button[aria-label*="Play"], .play-button, .track-play');
    
    // If no explicit buttons, try finding track rows
    if (playButtons.length === 0) {
      playButtons = container.querySelectorAll('.awp-track, .awp-playlist-item, [data-track-index]');
    }

    if (playButtons.length >= trackIndex) {
      const target = playButtons[trackIndex - 1];
      target.click();
    } else {
      console.warn(`AWPlayer: Track index ${trackIndex} out of range (Found ${playButtons.length} tracks)`);
    }
  }

  // Global event listener for deep links
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a');
    if (!a) return;
    
    const href = a.getAttribute('href');
    if (!href) return;

    const info = parseAwplayFromUrl(href);
    if (!info) return;

    e.preventDefault();

    // Keep URL state without reload
    history.pushState({}, '', href);

    // Optional scroll to player
    if (a.hash && a.hash.includes('play-audio')) {
      const el = document.querySelector('#play-audio');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    waitForAwplayerDom().then(() => playAwplayerTrack(info.trackIndex));
  }, true);

  // Handle on load
  document.addEventListener('DOMContentLoaded', () => {
    findAndStyleAwplayLinks(document);

    const info = parseAwplayFromUrl(window.location.href);
    if (!info) return;

    waitForAwplayerDom().then(() => playAwplayerTrack(info.trackIndex));
  });

  // Handle Shopify theme section reloads
  document.addEventListener('shopify:section:load', (e) => {
    findAndStyleAwplayLinks(e.target);
  });

})();
